import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { authWithExternalToken } from 'js/actions/session';
import { getCurrentUser } from 'js/reducers/session';
import { useApp } from '../contexts/AppContextProvider';
import { isAllowedEmbeddedParentOrigin } from 'js/constants/config';

/**
 * Custom hook to handle external token authentication in embedded mode using postMessage.
 *
 * Security considerations:
 * - Only works when embedded=true query parameter is present
 * - Listens for postMessage from parent window with authentication token
 * - Validates message origin using same-origin policy
 * - Clears any existing session data before using external token
 * - Only processes token once to prevent repeated authentication
 *
 * Usage:
 * From parent window:
 * window.frames['nexla-iframe'].postMessage({
 *   type: 'NEXLA_AUTH_TOKEN',
 *   token: 'your_access_token'
 * }, window.location.origin);
 */

interface AuthTokenMessage {
  type: 'NEXLA_AUTH_TOKEN';
  token: string;
}

const MAX_AUTH_RETRIES = 3;
const RETRY_BASE_MS = 1000;

export const useExternalTokenAuth = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { isEmbeddedMode } = useApp();
  const currentUser = useSelector(getCurrentUser);
  const isAuthenticatedRef = useRef(false);
  const isAuthInProgressRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isExternalTokenProcessed, setIsExternalTokenProcessed] = useState(false);

  // Allow re-auth after embedded session logout (e.g. token expiry) without reload.
  useEffect(() => {
    if (!isEmbeddedMode) {
      return;
    }
    if (!currentUser && isAuthenticatedRef.current) {
      isAuthenticatedRef.current = false;
      isAuthInProgressRef.current = false;
      lastTokenRef.current = null;
      retryCountRef.current = 0;
      setIsExternalTokenProcessed(false);
    }
  }, [currentUser, isEmbeddedMode]);

  useEffect(() => {
    if (!isEmbeddedMode) {
      return;
    }

    const clearRetryTimeout = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const sendReady = () => {
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'NEXLA_IFRAME_READY' }, '*');
      }
    };

    const handleMessage = (event: MessageEvent<AuthTokenMessage>) => {
      if (!isAllowedEmbeddedParentOrigin(event.origin)) {
        console.warn('Received postMessage from unauthorized origin:', event.origin);
        return;
      }

      if (event.data?.type !== 'NEXLA_AUTH_TOKEN') {
        return;
      }

      const token = event.data.token;

      if (!token || typeof token !== 'string') {
        console.error('Invalid token received via postMessage');
        if (event.source && 'postMessage' in event.source) {
          (event.source as Window).postMessage(
            {
              type: 'NEXLA_AUTH_ERROR',
              error: 'Invalid token provided',
            },
            event.origin,
          );
        }
        return;
      }

      if (isAuthenticatedRef.current) {
        return;
      }

      // A new (different) token from the parent resets the retry budget so a
      // transient failure burst does not permanently lock out a later valid
      // token. The same token re-sent during a retry keeps counting down.
      if (token !== lastTokenRef.current) {
        lastTokenRef.current = token;
        retryCountRef.current = 0;
      }

      // Synchronous in-flight guard: ignore duplicate token messages that
      // arrive before the current auth attempt resolves (prevents concurrent
      // /token/refresh calls and duplicate success/error signals to the parent).
      if (isAuthInProgressRef.current) {
        return;
      }

      if (retryCountRef.current >= MAX_AUTH_RETRIES) {
        console.warn('Max external token auth retries exceeded');
        return;
      }

      clearRetryTimeout();
      isAuthInProgressRef.current = true;

      // Clear stale session only when starting a new auth attempt
      localStorage.removeItem('nexSession');
      localStorage.removeItem('nex_org_id');
      localStorage.removeItem('nex_org_name');

      dispatch(authWithExternalToken(token))
        .then(() => {
          isAuthenticatedRef.current = true;
          setIsExternalTokenProcessed(true);
          retryCountRef.current = 0;

          if (event.source && 'postMessage' in event.source) {
            (event.source as Window).postMessage({ type: 'NEXLA_AUTH_SUCCESS' }, event.origin);
          }
        })
        .catch((error: unknown) => {
          retryCountRef.current += 1;
          console.error('Authentication failed:', error);

          const errorMessage = error instanceof Error ? error.message : 'Authentication failed';

          if (event.source && 'postMessage' in event.source) {
            (event.source as Window).postMessage(
              {
                type: 'NEXLA_AUTH_ERROR',
                error: errorMessage,
              },
              event.origin,
            );
          }

          if (retryCountRef.current < MAX_AUTH_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              sendReady();
            }, RETRY_BASE_MS * retryCountRef.current);
          }
        })
        .finally(() => {
          isAuthInProgressRef.current = false;
        });
    };

    window.addEventListener('message', handleMessage);
    sendReady();

    return () => {
      window.removeEventListener('message', handleMessage);
      clearRetryTimeout();
    };
  }, [isEmbeddedMode, dispatch]);

  return {
    isExternalTokenMode: isEmbeddedMode,
    isExternalTokenProcessed,
  };
};
